/**
 * Itens de navegação dos dois shells — **fonte única** (PRD §3.5.1, linhas 479–481).
 *
 * Existe separado porque os mesmos itens aparecem em **dois lugares**: os *bottom tabs* (mobile) e a
 * *sidebar* (desktop/tablet, só no shell profissional). Declará-los duas vezes garante que divergem
 * no primeiro ajuste — e um item presente num e ausente no outro é um bug que só aparece para quem
 * testa naquele viewport. Aqui, acrescentar um item muda as duas superfícies de uma vez.
 *
 * ⚠️ A ordem e os rótulos são **literais do PRD**. Não reordenar por preferência estética: a ordem
 * dos separadores é decisão de produto, e o teste `nav-items.test.ts` congela-a.
 */

/** Um destino de navegação. `labelKey` é chave i18n — nunca texto literal (ritual (c), T0.20). */
export interface NavItem {
  /** Identificador estável, usado em testes e instrumentação (T0.19). */
  id: string;
  /** Chave i18n. As traduções das 4 locales são consolidadas na T0.20. */
  labelKey: string;
  /** Rota absoluta. */
  to: string;
}

/**
 * Shell Consumidor — 5 separadores (PRD §3.5.1).
 * *"Home · Mapa · Agendamentos · Favoritos · Perfil. Rotas `/home/*`."*
 */
export const CONSUMER_NAV: readonly NavItem[] = [
  { id: 'home', labelKey: 'nav.consumer.home', to: '/home' },
  { id: 'map', labelKey: 'nav.consumer.map', to: '/home/mapa' },
  { id: 'bookings', labelKey: 'nav.consumer.bookings', to: '/home/agendamentos' },
  { id: 'favorites', labelKey: 'nav.consumer.favorites', to: '/home/favoritos' },
  { id: 'profile', labelKey: 'nav.consumer.profile', to: '/home/perfil' },
];

/**
 * Shell Profissional — 5 primários (PRD §3.5.1).
 * *"Dashboard · Agenda · Clientes · Promoções · Mais. Rotas `/dashboard/*`."*
 */
export const PROFESSIONAL_NAV: readonly NavItem[] = [
  { id: 'dashboard', labelKey: 'nav.pro.dashboard', to: '/dashboard' },
  { id: 'schedule', labelKey: 'nav.pro.schedule', to: '/dashboard/agenda' },
  { id: 'clients', labelKey: 'nav.pro.clients', to: '/dashboard/clientes' },
  { id: 'promotions', labelKey: 'nav.pro.promotions', to: '/dashboard/promocoes' },
  { id: 'more', labelKey: 'nav.pro.more', to: '/dashboard/mais' },
];

/**
 * Sub-itens do hub **"Mais"** (PRD §3.5.1).
 * *"agrega Serviços, Equipa, Fidelidade, Analytics, Faturação, Assinatura e Configurações."*
 *
 * São 7, e ficam fora dos primários de propósito: cinco separadores é o limite prático da *thumb
 * zone* num bottom tab bar (PRD §6.2). O hub é o que permite obedecer ao limite sem perder destino.
 */
export const PROFESSIONAL_MORE_NAV: readonly NavItem[] = [
  { id: 'services', labelKey: 'nav.pro.more_items.services', to: '/dashboard/servicos' },
  { id: 'team', labelKey: 'nav.pro.more_items.team', to: '/dashboard/equipa' },
  { id: 'loyalty', labelKey: 'nav.pro.more_items.loyalty', to: '/dashboard/fidelidade' },
  { id: 'analytics', labelKey: 'nav.pro.more_items.analytics', to: '/dashboard/analytics' },
  { id: 'billing', labelKey: 'nav.pro.more_items.billing', to: '/dashboard/faturacao' },
  { id: 'subscription', labelKey: 'nav.pro.more_items.subscription', to: '/dashboard/assinatura' },
  { id: 'settings', labelKey: 'nav.pro.more_items.settings', to: '/dashboard/configuracoes' },
];

/**
 * Alvo mínimo de toque, em píxeis CSS — `RNF-ACC-05` e PRD §6.2 (≥44×44 pt).
 *
 * **Reexportado de `@powerful/ui`, não redeclarado.** A T0.2 já o definiu ao construir o `Button`,
 * onde é imposto por `min-h`/`min-w` porque a altura nominal dos tamanhos menores fica abaixo do
 * mínimo. Declarar um segundo `44` aqui criaria duas fontes para o mesmo requisito contratual — e
 * a que ficasse desatualizada não faria barulho nenhum.
 */
export { MIN_TOUCH_TARGET_PX } from '@powerful/ui';
